import { useEffect, type ComponentPropsWithoutRef } from "react";
import { Icon } from "../Icon";
import { Flex } from "../Flex";

import styles from "./Combobox.module.scss";
import listboxStyles from "../Listbox/Listbox.module.scss";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import { useCombobox } from "./internal/ComboboxContext";
import { ComboboxItemAddNewProps, ComboboxItemProps } from "./ComboboxTypes";
import { useTrackingId } from "../../hooks";
import { ADD_NEW } from "./internal/ComboboxUtils";
import { ComboboxItemVisual } from "./internal/ComboboxItemVisual";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CoreComboboxItemElement = function <Item = any>({
  children,
  disabled: disabledProp,
  item,
  index: indexProp,
  hideCheckbox,
  forceCloseOnSelect,
  forceClearInputValueOnSelect,
  alignItems,
  justifyItems,
  alignContent,
  justifyContent,
  placeItems,
  placeContent,
  gap,
  rowGap,
  columnGap,
  ...rest
}: ComboboxItemProps<Item>) {
  const {
    itemToKey,
    getItemProps,
    multiple,
    selectedItem,
    selectedItems,
    disabledItems,
    setDisabledItems,
    setForceCloseOnSelectItems,
    setForceClearInputValueOnSelectItems,
    highlightedIndex,
    items,
  } = useCombobox<Item>();

  const index = item === ADD_NEW && !multiple ? indexProp : items.indexOf(item);

  const disabled =
    disabledProp ||
    (item && item instanceof Object && "disabled" in item && item.disabled);

  useEffect(() => {
    if (disabledProp && !disabledItems.includes(item)) {
      setDisabledItems([...disabledItems, item]);
    } else if (disabledItems.includes(item)) {
      setDisabledItems(disabledItems.filter((prevItem) => prevItem !== item));
    }

    return () => {
      if (disabledItems.includes(item)) {
        setDisabledItems(disabledItems.filter((prevItem) => prevItem !== item));
      }
    };
  }, [item, disabledProp, setDisabledItems]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (forceCloseOnSelect) {
      setForceCloseOnSelectItems((forceCloseOnSelectItems) => {
        if (forceCloseOnSelectItems.includes(item)) {
          return forceCloseOnSelectItems;
        } else {
          return [...forceCloseOnSelectItems, item];
        }
      });
    }

    return () => {
      setForceCloseOnSelectItems((forceCloseOnSelectItems) => {
        if (forceCloseOnSelectItems.includes(item)) {
          return forceCloseOnSelectItems.filter(
            (prevItem) => prevItem !== item,
          );
        } else {
          return forceCloseOnSelectItems;
        }
      });
    };
  }, [item, forceCloseOnSelect, setForceCloseOnSelectItems]);

  useEffect(() => {
    if (forceClearInputValueOnSelect) {
      setForceClearInputValueOnSelectItems(
        (forceClearInputValueOnSelectItems) => {
          if (forceClearInputValueOnSelectItems.includes(item)) {
            return forceClearInputValueOnSelectItems;
          } else {
            return [...forceClearInputValueOnSelectItems, item];
          }
        },
      );
    }

    return () => {
      setForceClearInputValueOnSelectItems(
        (forceClearInputValueOnSelectItems) => {
          if (forceClearInputValueOnSelectItems.includes(item)) {
            return forceClearInputValueOnSelectItems.filter(
              (prevItem) => prevItem !== item,
            );
          } else {
            return forceClearInputValueOnSelectItems;
          }
        },
      );
    };
  }, [
    item,
    forceClearInputValueOnSelect,
    setForceClearInputValueOnSelectItems,
  ]);

  const flexProps = {
    alignItems,
    justifyItems,
    alignContent,
    justifyContent,
    placeItems,
    placeContent,
    gap,
    rowGap,
    columnGap,
  };

  if (multiple) {
    const checked = selectedItems.some(
      (selectedItem) => itemToKey(selectedItem) === itemToKey(item),
    );

    return (
      <ComboboxItemVisual
        rootDivProps={getItemProps({
          item,
          index,
          "aria-selected": highlightedIndex === index,
          ...(rest as ComponentPropsWithoutRef<"div">),
        })}
        disabled={disabled as boolean}
        checked={checked}
        hideCheckbox={hideCheckbox || item === ADD_NEW}
        flexProps={flexProps}
      >
        {children}
      </ComboboxItemVisual>
    );
  } else {
    return (
      <li
        {...getItemProps({
          item,
          index,
          "aria-selected": highlightedIndex === index,
          ...(rest as ComponentPropsWithoutRef<"li">),
        })}
        data-anv="combobox-item"
        data-checked={itemToKey(selectedItem) === itemToKey(item)}
        className={styles["item"]}
        {...(disabled ? { "aria-disabled": true } : {})}
      >
        <Flex
          className={listboxStyles["children"]}
          grow={1}
          shrink={1}
          {...flexProps}
        >
          {children}
        </Flex>
        <Icon
          aria-hidden
          svg={Check}
          inherit
          className={styles["Icon"]}
          style={{
            opacity: itemToKey(selectedItem) === itemToKey(item) ? 1 : 0,
          }}
        />
      </li>
    );
  }
};

/**
 * ComboboxItem component for rendering individual items in a combobox list
 *
 * Features:
 * - Supports both single and multiple selection modes
 * - Handles disabled state for individual items
 * - Provides checkbox UI for multiple selection
 * - Supports custom layout with Flex props
 * - Integrates with combobox state management
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered combobox item
 *
 * @example
 * <ComboboxItem item={user} index={0}>
 *   <Text>{user.name}</Text>
 *   <Text subdued>{user.email}</Text>
 * </ComboboxItem>
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ComboboxItemElement = function <Item = any>(
  props: ComboboxItemProps<Item>,
) {
  return <CoreComboboxItemElement {...props} />;
};

/**
 * ComboboxItem component for rendering individual items in the list.
 *
 * Features:
 * - Individual item rendering with proper accessibility
 * - Support for disabled state
 * - Custom content rendering
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Combobox.Item item={item} index={index}>
 *   <Flex direction="column">
 *     <Text>{item.name}</Text>
 *     <Text size="small" subdued>{item.description}</Text>
 *   </Flex>
 * </Combobox.Item>
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ComboboxItem = function <Item = any>(
  props: ComboboxItemProps<Item>,
) {
  const data = {
    item: props.item,
  };

  const trackingId = useTrackingId({
    name: "ComboboxItem",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  return <ComboboxItemElement<Item> data-tracking-id={trackingId} {...props} />;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CoreComboboxItemAddNewElement = function <Item = any>({
  onSelection,
  ...props
}: ComboboxItemAddNewProps<Item>) {
  const { items, setHasAddNew, hasExactMatch, setOnSelectItemAddNew } =
    useCombobox<Item>();

  useEffect(() => {
    setHasAddNew?.(true);

    return () => {
      setHasAddNew?.(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOnSelectItemAddNew?.(() => onSelection);

    return () => {
      setOnSelectItemAddNew?.(undefined);
    };
  }, [setOnSelectItemAddNew, onSelection]);

  return !hasExactMatch ? (
    <ComboboxItem<Item>
      {...props}
      item={ADD_NEW as Item}
      index={items.length}
    />
  ) : null;
};

/**
 * ComboboxItemAddNew component for rendering an "Add New" option in a combobox
 *
 * Features:
 * - Allows users to add new items that don't exist in the list
 * - Integrates with combobox state management
 * - Supports custom layout with Flex props
 * - Handles selection callback for new item creation
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered "Add New" item or null
 *
 * @example
 * <ComboboxItemAddNew onSelection={(value) => createNewItem(value)}>
 *   <Text>Add "{inputValue}" as new item</Text>
 * </ComboboxItemAddNew>
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ComboboxItemAddNewElement = function <Item = any>(
  props: ComboboxItemAddNewProps<Item>,
) {
  return <CoreComboboxItemAddNewElement {...props} />;
};

/**
 * ComboboxItemAddNew component for rendering an "Add New" option.
 *
 * Features:
 * - "Add New" option that appears when no exact match is found
 * - Customizable content rendering
 * - Callback when selected
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Combobox.ItemAddNew onSelection={(inputValue) => addNewItem(inputValue)}>
 *   Add "{inputValue}" as new item
 * </Combobox.ItemAddNew>
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ComboboxItemAddNew = function <Item = any>(
  props: ComboboxItemAddNewProps<Item>,
) {
  const data = {
    item: "add-new",
  };

  const trackingId = useTrackingId({
    name: "ComboboxItemAddNew",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  return <ComboboxItemAddNewElement data-tracking-id={trackingId} {...props} />;
};
